import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const DEFAULT_BREAK_COLOR = "#000000";
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6})$/;
const BREAK_COLOR_FIELD = "Color";

const normalizeBreakColor = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (!raw) {
    return null;
  }
  if (HEX_COLOR_PATTERN.test(raw)) {
    return raw.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) {
    return `#${raw.toUpperCase()}`;
  }
  return null;
};

const isDuplicateBreakColor = (
  list: any[],
  color: string,
  excludeId?: string | null,
) => {
  const normalized = normalizeBreakColor(color);
  if (!normalized) {
    return false;
  }
  return (list ?? []).some((item) => {
    if (excludeId != null && String(item.id) === String(excludeId)) {
      return false;
    }
    return normalizeBreakColor(item[BREAK_COLOR_FIELD]) === normalized;
  });
};

const SpaBreakTypes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [breakCode, setBreakCode] = useState<string | null>(null);
  const [breakName, setBreakName] = useState<string | null>(null);
  const [breakColor, setBreakColor] = useState<string>(DEFAULT_BREAK_COLOR);
  const [isUtilized, setIsUtilized] = useState<boolean>(false);
  const [breakCodeFieldError, setBreakCodeFieldError] =
    useState<boolean>(false);
  const [breakCodeFieldErrorMessage, setBreakCodeFieldErrorMessage] =
    useState<string>("");
  const [breakNameFieldError, setBreakNameFieldError] =
    useState<boolean>(false);
  const [breakNameFieldErrorMessage, setBreakNameFieldErrorMessage] =
    useState<string>("");
  const [colorError, setColorError] = useState<boolean>(false);

  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateBreakTypeFields = (): boolean => {
    if (!breakCode || breakCode.length === 0) {
      setAlertMessage("Break Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setBreakCodeFieldError(false);
    setBreakCodeFieldErrorMessage("");
    if (
      !validateString(
        breakCode,
        validationLength.breakTypeCode,
        validationType.CODE,
      )
    ) {
      setBreakCodeFieldError(true);
      setBreakCodeFieldErrorMessage(validationMessages.breakTypeCode);
      return false;
    }

    if (!breakName || breakName.length === 0) {
      setAlertMessage("Break Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setBreakNameFieldError(false);
    setBreakNameFieldErrorMessage("");
    if (
      !validateString(
        breakName,
        validationLength.breakTypeName,
        validationType.NAME,
      )
    ) {
      setBreakNameFieldError(true);
      setBreakNameFieldErrorMessage(validationMessages.breakTypeName);
      return false;
    }

    setColorError(false);
    if (!normalizeBreakColor(breakColor)) {
      setColorError(true);
      setAlertMessage(
        "Color is mandatory. Enter a valid hex color (e.g. #000000).",
      );
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    const listForDuplicate = selectedId
      ? (items ?? []).filter((i: any) => String(i.id) !== String(selectedId))
      : (items ?? []);

    if (checkDuplicate(listForDuplicate, DNDFieldsEnum.BREAKCODE, breakCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (isDuplicateBreakColor(listForDuplicate, breakColor)) {
      setColorError(true);
      setAlertMessage("Cannot use the same color for multiple break types");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items ?? []);
    setSelectedId(checkData(items ?? [], selectedId) ? selectedId : null);
  }, [items]);

  const payload = {
    "Break Code": breakCode,
    "Break Name": breakName,
    [BREAK_COLOR_FIELD]:
      normalizeBreakColor(breakColor) ?? breakColor.toUpperCase(),
    IsUtilized: isUtilized,
  };

  const updateItem = () => {
    if (!validateBreakTypeFields()) {
      return false;
    }
    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.breakTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateBreakTypeFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...(prev ?? []), { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setBreakCode(list["Break Code"] ?? null);
      setBreakName(list["Break Name"] ?? null);
      setBreakColor(
        normalizeBreakColor(list[BREAK_COLOR_FIELD]) ?? DEFAULT_BREAK_COLOR,
      );
      setIsUtilized(
        parseSpaBooleanField(list?.IsUtilized ?? list?.Paid, false),
      );
      setColorError(false);
    } else {
      setBreakCode(null);
      setBreakName(null);
      setBreakColor(DEFAULT_BREAK_COLOR);
      setIsUtilized(false);
      setBreakCodeFieldError(false);
      setBreakCodeFieldErrorMessage("");
      setBreakNameFieldError(false);
      setBreakNameFieldErrorMessage("");
      setColorError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Break Types
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.breakTypeHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Break Types",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Break Code *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.breakTypeBreakTypeCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Break Code"
              value={breakCode ?? ""}
              error={breakCodeFieldError}
              helperText={breakCodeFieldError ? breakCodeFieldErrorMessage : ""}
              onChange={(e) => setBreakCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Break Name *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.breakTypeBreakTypeDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Break Name"
              value={breakName ?? ""}
              onChange={(e) => setBreakName(e.target.value || null)}
              helperText={breakNameFieldError ? breakNameFieldErrorMessage : ""}
              error={breakNameFieldError}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Color *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.breakTypeBreakTypeDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" gap="10px" alignItems="center">
              <TextField
                type="color"
                aria-label="Break type color"
                value={
                  HEX_COLOR_PATTERN.test(breakColor)
                    ? breakColor.toLowerCase()
                    : DEFAULT_BREAK_COLOR.toLowerCase()
                }
                onChange={(e) => {
                  setBreakColor(e.target.value.toUpperCase());
                  setColorError(false);
                }}
                sx={{ width: 72 }}
              />
              <TextField
                id="breakTypeColor"
                variant="outlined"
                placeholder="#000000"
                helperText={
                  colorError ? "Enter a valid hex color (e.g. #000000)" : ""
                }
                error={colorError}
                value={breakColor}
                onChange={(e) => {
                  setBreakColor(e.target.value);
                  setColorError(false);
                }}
                fullWidth
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={isUtilized}
                  onChange={(e) => setIsUtilized(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" alignItems="center">
                  Include in Utilization
                  <Tooltip
                    title={tooltipComponent(spaTooltipHeaders.breakTypeIncludeInUtilitization)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack>
              }
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Break Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Break Types Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={(e) => editItem(e)}
              onDelete={(e) => deleteItemById(e)}
              code={DNDFieldsEnum.BREAKCODE}
              name={DNDFieldsEnum.BREAKNAME}
            />
          ) : (
            <Typography variant="body2">No Break Types Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaBreakTypes;
